import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import { OrderRoom } from '../../types/orderRoom';
import { OrderRoomOccupant } from '../../types/orderRoomOccupant';
import { ConferenceHotelRoom } from '../../types/conferenceHotelRoom';
import { APIHOST } from '../../common';
import { formatDateFromYYYYMMDD } from '../../../lib/datetime';
import { OrderFormProps } from '../../lib/order';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditOrderRoomOccupantDialog from '../../components/dialog/EditOrderRoomOccupantDialog';
import { on } from 'events';

// API helper function to get authenticated headers
const getAuthHeaders = () => {
  const authToken = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(authToken && { Authorization: `Bearer ${authToken}` })
  };
};

const RoomOccupants = ({
  orderId,
  back,
  onSave,
  onStepComplete,
  type
}: OrderFormProps) => {
  const [orderRooms, setOrderRooms] = useState<OrderRoom[]>([]);
  const [conferenceHotelRooms, setConferenceHotelRooms] = useState<ConferenceHotelRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<OrderRoom | null>(null);
  const [editingOccupant, setEditingOccupant] = useState<OrderRoomOccupant | null>(null);

  // Load order rooms independently
  useEffect(() => {
    const loadOrderRooms = async () => {
      if (!orderId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
          throw new Error('No authentication token found');
        }

        // Fetch order with populated order_rooms
        const response = await fetch(`${APIHOST}/api/orders/${orderId}?populate[order_rooms][populate][0]=order_room_occupants&populate[order_rooms][populate][1]=conference_hotel_room`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to load order rooms');
        }
        
        const orderData = await response.json();
        setOrderRooms(orderData.data.order_rooms || []);
      } catch (err) {
        console.error('Error loading order rooms:', err);
        setError('Failed to load room data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadOrderRooms();
  }, [orderId]);

  // Load conference hotel rooms for max occupants validation
  useEffect(() => {
    const loadConferenceHotelRooms = async () => {
      try {
        const response = await fetch(`${APIHOST}/api/conference-hotel-rooms`, {
          headers: getAuthHeaders()
        });
        
        if (response.ok) {
          const data = await response.json();
          setConferenceHotelRooms(data.data || []);
        }
      } catch (err) {
        console.error('Error loading hotel rooms:', err);
      }
    };

    loadConferenceHotelRooms();
  }, []);

  

  // Get hotel room name helper
  const getHotelRoomName = (conferenceHotelRoom: string | ConferenceHotelRoom | null | undefined) => {
    if (!conferenceHotelRoom) return 'Unknown Room';
    
    if (typeof conferenceHotelRoom === 'string') {
      const room = conferenceHotelRooms.find(r => r.documentId === conferenceHotelRoom);
      return room ? room.name : 'Unknown Room';
    }
    
    return conferenceHotelRoom.name || 'Unknown Room';
  };

  // Get max occupants for a room
  const getMaxOccupants = (conferenceHotelRoom: string | ConferenceHotelRoom | null | undefined): number => {
    if (!conferenceHotelRoom) return 4;
    
    if (typeof conferenceHotelRoom === 'string') {
      const room = conferenceHotelRooms.find(r => r.documentId === conferenceHotelRoom);
      return room?.maxOccupants || 4;
    }
    
    return conferenceHotelRoom.maxOccupants || 4;
  };

  // Validation function
  const validateRooms = (): string[] => {
    const errors: string[] = [];
    
    if (orderRooms.length === 0) {
      errors.push('At least one room must be selected.');
      return errors;
    }

    orderRooms.forEach((room, index) => {
      if (!room.conference_hotel_room) {
        errors.push(`Room ${index + 1} is missing a selected hotel room.`);
      }
      if (!room.arrivalDate) {
        errors.push(`Room ${index + 1} is missing an arrival date.`);
      }
      if (!room.departureDate) {
        errors.push(`Room ${index + 1} is missing a departure date.`);
      }
      
      const occupants = room.order_room_occupants || [];
      if (occupants.length === 0) {
        errors.push(`Room ${index + 1} must have at least one occupant.`);
      } else {
        // Check max occupants constraint
        const maxOccupants = getMaxOccupants(room.conference_hotel_room);
        if (occupants.length > maxOccupants) {
          errors.push(`Room ${index + 1} has ${occupants.length} occupants but maximum allowed is ${maxOccupants}.`);
        }
        
        // Validate occupant data
        occupants.forEach((occupant, occIndex) => {
          if (!occupant.firstName?.trim()) {
            errors.push(`Occupant ${occIndex + 1} in Room ${index + 1} is missing a first name.`);
          }
          if (!occupant.lastName?.trim()) {
            errors.push(`Occupant ${occIndex + 1} in Room ${index + 1} is missing a last name.`);
          }
        });
      }
    });
    
    return errors;
  };

  // API functions for occupant management
  const deleteOccupantFromAPI = async (documentId: string) => {
    try {
      const response = await fetch(`${APIHOST}/api/order-room-occupants/${documentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        onSave && onSave(); // Trigger save callback after successful API operation
      }
      return response.ok;
    } catch (error) {
      console.error('Error deleting occupant:', error);
      return false;
    }
  };

  const addOccupant = (roomId: string) => {
    // Check max occupants constraint before adding
    const room = orderRooms.find(r => r.id === roomId);
    if (!room) return;
    
    const currentOccupants = room.order_room_occupants?.length || 0;
    const maxOccupants = getMaxOccupants(room.conference_hotel_room);
    
    if (currentOccupants >= maxOccupants) {
      alert(`Cannot add more occupants. Maximum allowed for this room type is ${maxOccupants}.`);
      return;
    }
    if (room) {
      const currentOccupants = room.order_room_occupants?.length || 0;
      const maxOccupants = getMaxOccupants(room.conference_hotel_room);
      
      if (currentOccupants >= maxOccupants) {
        alert(`Cannot add more occupants. Maximum allowed for this room type is ${maxOccupants}.`);
        return;
      }
    }
    
    // Open dialog for adding new occupant
    setSelectedRoom(room);
    setEditingOccupant(null);
    setDialogOpen(true);
  };

  const editOccupant = (roomId: string, occupant: OrderRoomOccupant) => {
    const room = orderRooms.find(r => r.id === roomId);
    setSelectedRoom(room || null);
    setEditingOccupant(occupant);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedRoom(null);
    setEditingOccupant(null);
  };

  const handleDialogSave = async () => {
    // Reload data to reflect changes
    const response = await fetch(`${APIHOST}/api/orders/${orderId}?populate[order_rooms][populate][0]=order_room_occupants&populate[order_rooms][populate][1]=conference_hotel_room`, {
      headers: getAuthHeaders()
    });
    
    if (response.ok) {
      const orderData = await response.json();
      setOrderRooms(orderData.data.order_rooms || []);
      onSave && onSave(); // Trigger save callback after successful API operation
    }
  };

  const removeOccupant = async (roomId: string, occupantId: string) => {
    const room = orderRooms.find(r => r.id === roomId);
    if (room) {
      const occupant = room.order_room_occupants?.find(o => o.id === occupantId);
      
      // Delete from API first if occupant has documentId
      if (occupant?.documentId) {
        const deleted = await deleteOccupantFromAPI(occupant.documentId);
        if (!deleted) {
          console.error('Failed to delete occupant from API');
          return;
        }
      }
      
      const updatedOrderRooms = orderRooms.map(r => {
        if (r.id === roomId) {
          const updatedOccupants = (r.order_room_occupants || []).filter(o => o.id !== occupantId);
          return { ...r, order_room_occupants: updatedOccupants };
        }
        return r;
      });
      
      setOrderRooms(updatedOrderRooms);
    }
  };

  // Save all occupant data to API (simplified since individual saves happen on dialog close)
  const saveAllRoomData = async () => {
    setSaving(true);
    try {
      // Just reload data to ensure it's in sync with the backend
      const response = await fetch(`${APIHOST}/api/orders/${orderId}?populate[order_rooms][populate][0]=order_room_occupants&populate[order_rooms][populate][1]=conference_hotel_room`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const orderData = await response.json();
        setOrderRooms(orderData.data.order_rooms || []);
      }
    } catch (error) {
      console.error('Error reloading occupant data:', error);
      setError('Failed to reload occupant data. Please refresh the page.');
    } finally {
      setSaving(false);
    }
  };



  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h3">
          Room Occupants
        </Typography>
        {saving && <CircularProgress size={24} />}
      </Box>

      {orderRooms.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No rooms selected yet. Please select rooms first before adding occupants.
        </Alert>
      ) : (
        orderRooms.map((room, index) => {
          const maxOccupants = getMaxOccupants(room.conference_hotel_room);
          const currentOccupants = room.order_room_occupants?.length || 0;
          const canAddMore = currentOccupants < maxOccupants;
          
          return (
            <Card key={room.id} sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" component="h4">
                    Room {index + 1}
                  </Typography>
                  <Chip 
                    label={`${currentOccupants}/${maxOccupants} occupants`}
                    color={currentOccupants > maxOccupants ? 'error' : 'default'}
                    size="small"
                  />
                </Box>

                {/* Room Information Display */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3, mb: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Hotel Room
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getHotelRoomName(room.conference_hotel_room)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Room Type
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                      {room.type || 'Not specified'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Arrival Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatDateFromYYYYMMDD (room.arrivalDate)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Departure Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatDateFromYYYYMMDD (room.departureDate)}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" component="h5">
                    Occupants
                  </Typography>
                  <Button
                    variant="text"
                    startIcon={<PersonAddIcon />}
                    onClick={() => addOccupant(room.id)}
                    size="small"
                    disabled={!canAddMore}
                  >
                    Add Occupant
                  </Button>
                </Box>

                {currentOccupants > maxOccupants && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    This room has too many occupants. Maximum allowed: {maxOccupants}
                  </Alert>
                )}

                {!room.order_room_occupants || room.order_room_occupants.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No occupants added yet. Click "Add Occupant" to add people to this room.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {room.order_room_occupants.map((occupant) => (
                      <Box key={occupant.id} sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1
                      }}>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {occupant.firstName} {occupant.lastName}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            onClick={() => editOccupant(room.id, occupant)}
                            color="primary"
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => removeOccupant(room.id, occupant.id)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })
      )}

      <Box>
        <Button
          color="primary"
          size="small"
          startIcon={<ArrowBackIcon fontSize="small" />}
          sx={{px: 4, py: 0.5, mt: 2}}
          onClick={back} 
          disabled={!back} >
          Back
        </Button>
        <Button
          color="primary"
          size="small"
          variant="contained"
          startIcon={<ArrowForwardIcon fontSize="small" />}
          sx={{px: 4, py: 0.5, mt: 2}}
          onClick={onStepComplete}
        >
          {type === 'step' ? 'Next' : 'Save'}
        </Button>
      </Box>


      {/* Add/Edit Occupant Dialog */}
      {selectedRoom && (
        <EditOrderRoomOccupantDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          occupant={editingOccupant}
          orderRoomId={selectedRoom.documentId || selectedRoom.id}
          maxOccupants={getMaxOccupants(selectedRoom.conference_hotel_room)}
          currentOccupantCount={selectedRoom.order_room_occupants?.length || 0}
          onSave={handleDialogSave}
        />
      )}
    </Box>
  );
};

RoomOccupants.displayName = 'RoomOccupants';
export default RoomOccupants;